import React from 'react'
import { Typography, Grid } from '@material-ui/core'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import EmptySection from '../../../../../atoms/EmptySection'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import Switch from '../../../../../molecules/Switch'
import OpalCard from '../../../../../opal/OpalCard'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'
import ConnectionFormField from './ConnectionFormField'
import InitiateOAuthFlowButton from './InitiateOAuthFlowButton'

function DomainConnectionForm ({ template, form, processing }) {
  const requestDelegation = form.watch('requestDelegation')
  const authRequiredFields = template?.type === 'oauth' && template?.fields
    ? template?.fields.filter(f => f.type !== 'oauth')
    : []
  const authRequiredFieldValuesObj = {}
  authRequiredFields.forEach(field => {
    authRequiredFieldValuesObj[field.name] = form.watch(`connectionArgs.${field.name}`)
  })

  const hasEmptyRequiredFields = template?.type === 'oauth' && template?.fields ? template?.fields.filter(f => f.type !== 'oauth').some((field) => {
    const isRequired = field.rules?.required?.value === true
    const fieldValue = authRequiredFieldValuesObj[field.name]
    return isRequired && (!fieldValue || fieldValue === '' || fieldValue === undefined)
  }) : false
  if (!template) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <EmptySection title='Not Available' description='This connection cannot be created' />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant='h3'>{template.description}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='code'
          rules={{
            required: { value: true, message: 'Required' },
            maxLength: { value: 16, message: 'Maximum 16 characters' }
          }}
          render={(f) => (
            <SydLabel label='Connection Code' error={hookFormErrorAdapter(form, f.fieldState)}>
              <SydInput size='sm' {...f.field} disabled={processing} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          control={form.control}
          name='displayName'
          rules={{
            required: { value: true, message: 'Required' },
            maxLength: { value: 96, message: 'Maximum 96 characters' }
          }}
          render={(f) => (
            <SydLabel label='Name' error={hookFormErrorAdapter(form, f.fieldState)}>
              <SydInput size='sm' {...f.field} disabled={processing} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={form.control}
          name='description'
          render={(f) => (
            <SydLabel label='Description'>
              <textarea {...f.field} disabled={processing} style={{ width: '100%', minHeight: '100px' }} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <OpalCard size='md'>
          <div className='mb-5'>
            <Controller
              control={form.control}
              name='requestDelegation'
              render={(f) => (
                <SydLabel label='Request for an Administrator to Enter Credentials'>
                  <Switch {...f.field} disabled={processing} />
                </SydLabel>
              )}
            />
          </div>
          {requestDelegation ? (
            <div className='text-size-sm py-2'>The credentials will be delegated for an admin to input</div>
          ) : (
            <FadeIn>
              {template.type === 'oauth' ? (
                <Grid container spacing={1}>
                  {(template.fields || []).map(field => (
                    field.type !== 'oauth' ? (
                      <Grid item xs={12} key={`field_${field.name}`}>
                        <Controller
                          control={form.control}
                          name={`connectionArgs.${field.name}`}
                          rules={field.rules}
                          render={(f) => (
                            <SydLabel label={field.label || field.name} error={hookFormErrorAdapter(form, f.fieldState)}>
                              <ConnectionFormField type={field.type} {...f.field} disabled={processing} />
                            </SydLabel>
                          )}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} key={`field_${field.name}`}>
                        <Controller
                          control={form.control}
                          name='connectionArgs.authorizationId'
                          rules={{
                            required: { value: true, message: 'Required' }
                          }}
                          render={(f) => (
                            <SydLabel label='' error={hookFormErrorAdapter(form, f.fieldState)}>
                              <InitiateOAuthFlowButton
                                config={template.config}
                                label={template.label}
                                value={f.field.value}
                                onChange={f.field.onChange}
                                authRequiredFieldValues={authRequiredFieldValuesObj}
                                disabled={hasEmptyRequiredFields}
                              />
                            </SydLabel>
                          )}
                        />
                      </Grid>
                    )
                  ))}

                </Grid>
              ) : (
                <Grid container spacing={1}>
                  {(template.fields || []).map(field => (
                    <Grid item xs={12} key={`field_${field.name}`}>
                      <Controller
                        control={form.control}
                        name={`connectionArgs.${field.name}`}
                        rules={field.rules}
                        render={(f) => (
                          <SydLabel label={field.label || field.name} error={hookFormErrorAdapter(form, f.fieldState)}>
                            <ConnectionFormField type={field.type} {...f.field} disabled={processing} />
                          </SydLabel>
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </FadeIn>
          )}
        </OpalCard>
      </Grid>
    </Grid>
  )
}

DomainConnectionForm.propTypes = {
  template: PropTypes.shape({
    description: PropTypes.string,
    type: PropTypes.string,
    fields: PropTypes.array,
    config: PropTypes.any,
    label: PropTypes.string
  }),
  form: PropTypes.shape({
    reset: PropTypes.func,
    control: PropTypes.any,
    watch: PropTypes.func
  }),
  processing: PropTypes.bool
}

export default DomainConnectionForm
